import React from 'react';

import { Card, Col, Row, Statistic } from 'antd';

import { PERFORMANCE_UI } from '@/shared/lib/constants/performance';
import { formatBytes } from '@/shared/lib/utils/formatBytes';

import type { PerformanceStats as PerformanceStatsType } from '@/entities/performance/domain/types';

import styles from './PerformanceStats.module.css';

type PerformanceStatsProps = {
  stats: PerformanceStatsType;
  selectedType: string | null;
  onSelectType: (type: string) => void;
};

export const PerformanceStats: React.FC<PerformanceStatsProps> = ({ stats, selectedType, onSelectType }) => {
  const handleCardClick = (type: string, count: number) => {
    if (count > 0) {
      onSelectType(type);
    }
  };

  const isSelected = (type: string) => selectedType === type;

  return (
    <div className={styles.statsContainer}>
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h4>에러 현황</h4>
          <span className={styles.sectionSubtitle}>최근 24시간</span>
        </div>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={8}>
            <Card
              className={`${styles.statCard} ${stats.errors.apiErrorCount > 0 ? styles.clickableValue : ''} ${
                isSelected('api_response_time') ? styles.selectedCard : ''
              }`}
              onClick={() => handleCardClick('api_response_time', stats.errors.apiErrorCount)}>
              <Statistic
                title="API 에러"
                value={stats.errors.apiErrorCount}
                suffix="건"
                valueStyle={{
                  color: '#ff4d4f',
                  fontSize: PERFORMANCE_UI.LARGE_STAT_FONT_SIZE,
                  fontWeight: 600,
                }}
              />
            </Card>
          </Col>

          <Col xs={24} sm={8}>
            <Card
              className={`${styles.statCard} ${stats.errors.connectionResetCount > 0 ? styles.clickableValue : ''} ${
                isSelected('connection_reset_retry') ? styles.selectedCard : ''
              }`}
              onClick={() => handleCardClick('connection_reset_retry', stats.errors.connectionResetCount)}>
              <Statistic
                title="Connection Reset"
                value={stats.errors.connectionResetCount}
                suffix="건"
                valueStyle={{
                  color: '#fa8c16',
                  fontSize: PERFORMANCE_UI.LARGE_STAT_FONT_SIZE,
                  fontWeight: 600,
                }}
              />
            </Card>
          </Col>

          <Col xs={24} sm={8}>
            <Card
              className={`${styles.statCard} ${stats.errors.timeoutCount > 0 ? styles.clickableValue : ''} ${
                isSelected('timeout') ? styles.selectedCard : ''
              }`}
              onClick={() => handleCardClick('timeout', stats.errors.timeoutCount)}>
              <Statistic
                title="타임아웃"
                value={stats.errors.timeoutCount}
                suffix="건"
                valueStyle={{
                  color: '#722ed1',
                  fontSize: PERFORMANCE_UI.LARGE_STAT_FONT_SIZE,
                  fontWeight: 600,
                }}
              />
            </Card>
          </Col>
        </Row>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h4>느린 쿼리</h4>
          <span className={styles.sectionSubtitle}>응답시간 3초 이상</span>
        </div>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12}>
            <Card
              className={`${styles.statCard} ${stats.slowQuery.count > 0 ? styles.clickableValue : ''} ${
                isSelected('slow_query') ? styles.selectedCard : ''
              }`}
              onClick={() => handleCardClick('slow_query', stats.slowQuery.count)}>
              <Statistic
                title="느린 쿼리 개수"
                value={stats.slowQuery.count}
                suffix="건"
                valueStyle={{
                  color: '#faad14',
                  fontSize: PERFORMANCE_UI.LARGE_STAT_FONT_SIZE,
                  fontWeight: 600,
                }}
              />
            </Card>
          </Col>

          <Col xs={24} sm={12}>
            <Card className={styles.statCard}>
              <Statistic
                title="평균 응답 크기"
                value={formatBytes(stats.slowQuery.avgResponseSize)}
                valueStyle={{ color: '#13c2c2', fontSize: PERFORMANCE_UI.LARGE_STAT_FONT_SIZE, fontWeight: 600 }}
              />
            </Card>
          </Col>
        </Row>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h4>응답시간 통계</h4>
          <span className={styles.sectionSubtitle}>성공 쿼리 기준 (세션)</span>
        </div>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={6}>
            <Card className={styles.statCard}>
              <Statistic
                title="평균"
                value={stats.successQuery.avgDuration}
                suffix="ms"
                valueStyle={{ color: '#52c41a', fontSize: PERFORMANCE_UI.MEDIUM_STAT_FONT_SIZE, fontWeight: 600 }}
              />
            </Card>
          </Col>

          <Col xs={24} sm={6}>
            <Card className={styles.statCard}>
              <Statistic
                title="최소"
                value={stats.successQuery.minDuration}
                suffix="ms"
                valueStyle={{ color: '#1890ff', fontSize: PERFORMANCE_UI.MEDIUM_STAT_FONT_SIZE, fontWeight: 600 }}
              />
            </Card>
          </Col>

          <Col xs={24} sm={6}>
            <Card className={styles.statCard}>
              <Statistic
                title="최대"
                value={stats.successQuery.maxDuration}
                suffix="ms"
                valueStyle={{ color: '#fa8c16', fontSize: PERFORMANCE_UI.MEDIUM_STAT_FONT_SIZE, fontWeight: 600 }}
              />
            </Card>
          </Col>

          <Col xs={24} sm={6}>
            <Card className={styles.statCard}>
              <Statistic
                title="평균 응답 크기"
                value={formatBytes(stats.successQuery.avgResponseSize)}
                valueStyle={{ color: '#13c2c2', fontSize: PERFORMANCE_UI.MEDIUM_STAT_FONT_SIZE, fontWeight: 600 }}
              />
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};
